import { useEffect, useRef, useState } from 'react'

import throttle from '@web/libs/lodash/throttle'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'

type ScrollData = {
  scrollY: number
  direction: ScrollDirection
}

export type ScrollDirection = 'up' | 'down' | null

type Props = AddEventListenerOptions & {
  root?: Element | null
  skip?: boolean
  onScrollChange?: (scroll: ScrollData) => void
}

const useScrollDirection = (props?: Props) => {
  const { root, skip, onScrollChange, ...options } = props ?? {}
  const [direction, setDirection] = useState<'up' | 'down' | null>(null)
  const [isTop, setIsTop] = useState<boolean>(true)

  const previousScroll = useRef<number>(0)

  const handleScroll = (e?: Event) => {
    const scrollY = root ? root.scrollTop : window.scrollY
    if (scrollY === previousScroll.current) return

    const isDragToBottom = scrollY < 0
    if (isDragToBottom) return

    const direction = previousScroll.current < scrollY ? 'down' : 'up'

    setDirection(direction)
    setIsTop(scrollY === 0)
    onScrollChange?.({ scrollY, direction })

    previousScroll.current = scrollY
  }

  const handleScrollThrottle = throttle((e?: Event) => {
    handleScroll(e)
  }, 200)

  useEffect(() => {
    if (skip) return
    window.addEventListener('scroll', handleScrollThrottle, options)
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('scroll', handleScrollThrottle, options)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip])

  return {
    scrollDirection: direction,
    isTop,
    setDirection,
  }
}

export default useScrollDirection
